import React, { useContext, useEffect, useState } from 'react';
import FaqService from '~/api/services/faq';
import { useTranslation } from 'react-i18next';
import { El } from './style';
import { RouteTypes } from '~/routes/constants';
import { Form, Header, Item, Label, Segment, Tab, TabPane } from 'semantic-ui-react';
import { FormFields } from '~/components/FormFields';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '~/components/Layout';
import notify from '~/components/Notify';
import Attachments from './QuestionAttachments/index';
import { El as ElLayout } from '~/components/Layout/style'
import { ThemeContext } from 'styled-components';
import { useAuth } from '~/routes/components/AuthProvider';
import { StyledMenuItem } from '../CloudStorage/style';
import { handleErrors } from '~/utils/helpers';

const CreateEditFaq: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { firstColor } = useContext(ThemeContext)
  const { id } = useParams();
  const { systemData: { texts } } = useAuth()
  const faqText = texts.find(text => text.slug === 'faq_text')
  const title = faqText?.translation || ''

  const [isBusy, setIsBusy] = useState(false);
  const [options, setOptions] = useState(null);
  const [activeIndex, setActiveIndex] = useState<string | number>(0);

  const { handleSubmit, control, reset, getValues, formState } = useForm({
    mode: 'onSubmit'
  });

  const questionsArray = useFieldArray({
    control,
    name: 'questions'
  });

  useEffect(() => {
    fetchFaqOptions();
  }, [t]);

  async function fetchFaqOptions() {
    setIsBusy(true);
    try {
      const responseOptions = await FaqService.getFaqOptions();
      setOptions(responseOptions.data);
      if (Object.keys(getValues()).length === 1) {
        let responseEdit = {
          data: {
            languages: [],
            questions: [{ languages: [] }]
          }
        };
        if (id) {
          responseEdit = await FaqService.getFaqGroupEditData(id);
          responseEdit?.data.languages.reverse();
        }
        responseOptions.data.languages.map((language) => {
          const index = responseEdit?.data?.languages.findIndex(
            (languageText) => languageText.id === language.id
          );
          if (index < 0) {
            responseEdit?.data?.languages.push({
              id: language.id
            });
            responseEdit?.data?.questions.map((item) => {
              item.languages.push({
                id: language.id,
                files: []
              });
            });
          }
        });
        reset(responseEdit.data);
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  const renderActions = () => {
    return (
      <>
        <ElLayout.PageHeaderActionButton>
          {t(id ? 'Update' : 'Create')}
        </ElLayout.PageHeaderActionButton>
        <ElLayout.PageHeaderActionButton
          onClick={() => navigate(RouteTypes.FAQ)}
        >
          {t('Cancel')}
        </ElLayout.PageHeaderActionButton>
      </>
    );
  };
  function addNewFields() {
    const fields = options?.languages.map((lang) => {
      return {
        id: lang.id,
        question: '',
        answer: '',
        files: []
      };
    });
    questionsArray.append({ languages: fields });
  }

  function renderQuestionsContent(languageOptions, indexLanguage) {
    return <Item.Group >
      {questionsArray.fields.map((item, index) => {
        return <Item key={item.id}>
          <div style={{ minWidth: '30%', maxWidth: '30%', marginTop: 20 }}>
            <Attachments
              index={index}
              languageIndex={indexLanguage}
              control={control}
              getValues={getValues}
            />
          </div>
          <Item.Content>
            <Item.Header as='a' style={{ width: '100%' }}>
              <FormFields.TextArea
                rows={1}
                key={languageOptions.id}
                placeholder={t('Question')}
                label={t('Question')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter question')
                  },
                  maxLength: {
                    value: 3000,
                    message: t('Max char is 3000')
                  }
                }}
                control={control}
                name={`questions[${index}].languages[${indexLanguage}].question`}
              ></FormFields.TextArea>
            </Item.Header>
            <Item.Description>
              <FormFields.TextArea
                rows={2}
                key={languageOptions.id}
                label={t('Answer')}
                placeholder={t('Answer')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter answer')
                  },
                  maxLength: {
                    value: 3000,
                    message: t('Max char is 3000')
                  }
                }}
                control={control}
                name={`questions[${index}].languages[${indexLanguage}].answer`}
              ></FormFields.TextArea>
            </Item.Description>
            <Item.Extra style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end' }}>
              {index !== 0 && <Form.Button
                onClick={() => questionsArray.remove(index)}
                size={'small'}
                basic
                color='red'
                type={'button'}
                icon='trash'
              />
              }
            </Item.Extra>
          </Item.Content>
        </Item>
      }
      )}
    </Item.Group>
  }

  function renderFormContentArea(languageOptions, index) {
    const arrayLanguage = getValues('languages') || options?.languages
    const indexLanguage = arrayLanguage.findIndex((language) => language.id === languageOptions.id)
    return (
      <div key={languageOptions.id}>
        <FormFields.Input
          label={t('Group')}
          placeholder={t('Group')}
          rules={{
            required: {
              value: true,
              message: t('Please enter group name')
            },
            maxLength: {
              value: 100,
              message: t('Max number of char. is 100')
            }
          }}
          key={languageOptions.id}
          control={control}
          name={`languages[${indexLanguage}].name`}
        />
        <Header attached='top' block>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            {t('Questions')}
            <Label
              onClick={() => addNewFields()}
              style={{ background: firstColor, color: 'white', cursor: 'pointer' }}>
              {t('Add new question')}
            </Label>
          </div>
        </Header>
        <Segment attached>
          {renderQuestionsContent(languageOptions, index)}
        </Segment>
      </div>
    );
  }

  function getMenuItem(pane, index) {
    const languagesErrors = formState.errors?.languages;
    const questionErrors = formState.errors?.questions ? formState.errors?.questions[index]?.languages : [];
    const paneHasError = index in (languagesErrors || {});
    const paneHasQError = index in (questionErrors || {});

    return <StyledMenuItem
      key={pane + index}
      className='tab-item'
    >
      <div className={`${paneHasError || paneHasQError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  function renderForm() {
    const tabs = options?.languages?.map((language, index) => {
      return {
        menuItem: getMenuItem(language?.label || '', index),
        render: () => renderFormContentArea(language, index)
      };
    });

    return (
      <>
        <div style={{ margin: 15 }}>
          <El.RolesContainer>
            <FormFields.Select
              label={t('Visible for')}
              control={control}
              options={options?.roles.map(role => ({ value: role.id, text: role.label })) || []}
              name={'roles'}
              rules={{
                required: {
                  value: true,
                  message: t('Please select roles')
                }
              }}
              selectOnBlur={false}
              placeholder={t('Visible for')}
              selection
              multiple
            />
          </El.RolesContainer>
        </div>
        <Tab
          menu={{ secondary: true, pointing: true }}
          activeIndex={activeIndex}
          renderActiveOnly={false}
          // @ts-ignore: semantic type error
          onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
          panes={tabs?.map((pane, index) => {
            return {
              menuItem: pane.menuItem,
              pane: <TabPane
                active={index === activeIndex}
                style={{ border: 'none' }}
                key={pane.menuItem + index}
              >
                {renderFormContentArea(options?.languages[index], index)}
              </TabPane>
            }
          })}
        />
      </>
    );
  }

  function removeUntouchedFiles(questionsData) {
    questionsData.map((question, questionIndex) => {
      question?.languages.map((lang, langIndex) => {
        if (lang?.files?.length > 0) {
          lang?.files?.map((file, fileIndex) => {
            if (!file?.attachment) {
              questionsData[questionIndex]?.languages[langIndex]?.files.splice(fileIndex, 1)
            }
          })
        }
      })
    })
    return questionsData
  }

  async function onSubmit(data: any) {
    for (let i = 0; i < data?.roles.length; i++) {
      if (data?.roles[i] === null || data?.roles[i] === undefined || data?.roles[i] === 0) {
        data?.roles.splice(i, 1);
        i--;
      }
    }
    data.questions = removeUntouchedFiles(data.questions)
    setIsBusy(true);
    try {
      if (id) {
        await FaqService.updateFaqGroup(id, data);
        notify(t('FAQ group has been successfully updated'), 'success');
      } else {
        await FaqService.createFaqGroup(data);
        notify(t('FAQ group has been successfully created'), 'success');
      }
      navigate(RouteTypes.FAQ);
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  return (
    <Layout
      isBusy={isBusy}
      title={id ? t('Edit {{title}} group', { title }) : t('Add {{title}} group', { title })}
      close={RouteTypes.FAQ}
      segmented
      actions={renderActions()}
      onSubmit={handleSubmit(onSubmit)}
    >
      {options && !isBusy && renderForm()}
    </Layout>
  );
};

export default CreateEditFaq;
